import styled from "@emotion/styled";

import { Text } from "../atoms/Text";
import { CollapsibleDrawer } from "../molecules/CollapsibleDrawer";
import { Table } from "../molecules/Table";

export interface ComparativaRow {
  cycle: number;
  nominal: number;
  efectivo: number;
  tasaSemanal: number | null;
  tasaDiaria: number | null;
}

export interface ComparativeOfertaDrawerProps {
  /** Filas de la tabla comparativa (p. ej. resultado.cyclePayments o comparativa del API). Si no se pasa, se muestran solo los textos informativos. */
  readonly rows?: ComparativaRow[];
  /** Financiera con la que se compara (después de "vs"). Corresponde a oferta.datosFormulario.financiera. */
  readonly financiera?: string;
}

const TitleWrapper = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ReferenceBox = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(3)};
  padding: ${(props) => props.theme.spacing(2)};
  border-left: 4px solid ${(props) => props.theme.colors.accent.A[600]};
  background: ${(props) => props.theme.colors.neutral.greyDark[1000]};
  border-radius: 0 ${(props) => props.theme.shape.borderRadiusLarge} ${(props) => props.theme.shape.borderRadiusLarge} 0;
`;

const ReferenceParagraph = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(1.5)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const TableSection = styled.div`
  margin-top: ${(props) => props.theme.spacing(2)};
`;

function formatNumber(value: unknown): string {
  if (value == null || typeof value !== "number") return "—";
  return value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function asString(value: unknown): string {
  if (value == null || typeof value !== "number") return "—";
  return String(value);
}

const COMPARATIVA_COLUMNS = [
  { id: "cycle" as const, label: "Ciclo", align: "right" as const },
  { id: "nominal" as const, label: "Nominal ($/mil)", align: "right" as const, format: (v: unknown) => formatNumber(v) },
  { id: "efectivo" as const, label: "Efectivo ($/mil)", align: "right" as const, format: (v: unknown) => formatNumber(v) },
  { id: "tasaSemanal" as const, label: "Tasa semanal %", align: "right" as const, format: (v: unknown) => asString(v) },
  { id: "tasaDiaria" as const, label: "Tasa diaria %", align: "right" as const, format: (v: unknown) => asString(v) },
];

const DEFAULT_VS_LABEL = "oferta genérica";

function formatFinancieraLabel(value: string | undefined): string {
  if (!value?.trim()) return DEFAULT_VS_LABEL;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function ComparativeOfertaDrawer({
  rows = [],
  financiera,
}: ComparativeOfertaDrawerProps) {
  const vsLabel = formatFinancieraLabel(financiera);

  return (
    <CollapsibleDrawer
      defaultTriggerLabel="Comparación Progresiva:"
      defaultTriggerAriaLabel="Mostrar u ocultar información adicional de la oferta"
      panelAriaLabel="Comparación progresiva de la oferta"
      id="comparative-oferta-info"
      autoScroll={true}
    >
      {rows.length > 0 && (
        <>
          <TitleWrapper>
            <Text variant="body3" weight="bold" color="highEmphasis">
              Podemos vs {vsLabel}
            </Text>
          </TitleWrapper>

          <ReferenceBox>
            <ReferenceParagraph>
              <Text variant="body4" weight="bold" color="mediumEmphasis">
                Lógica progresiva:
              </Text>{" "}
              <Text variant="body4" weight="regular" color="mediumEmphasis">
                Si traes 10 ciclos, tu ciclo 1 con nosotros se compara con tu ciclo 11 en Compartamos. Curva A/B/C: Mejoras rápidas al inicio, medias después, suaves al final.
              </Text>
            </ReferenceParagraph>
            <ReferenceParagraph>
              <Text variant="body4" weight="bold" color="mediumEmphasis">
                Nominal vs Efectivo:
              </Text>{" "}
              <Text variant="body4" weight="regular" color="mediumEmphasis">
                El pago efectivo solo aplica cumpliendo las primeras 15 semanas; si no, pagas el nominal completo.
              </Text>
            </ReferenceParagraph>
            <ReferenceParagraph>
              <Text variant="body4" weight="regular" color="mediumEmphasis">
                Verde = ahorras dinero, Morado = pagas un poco más.
              </Text>
            </ReferenceParagraph>
          </ReferenceBox>

          <TableSection>
            <Table<ComparativaRow>
              columns={COMPARATIVA_COLUMNS}
              data={rows}
              rowKey="cycle"
              aria-label="Comparativa de ciclos y tasas"
            />
          </TableSection>
        </>
      )}
    </CollapsibleDrawer>
  );
}
